import { ReviewCanvasContext } from 'modules/match-video/domain/interfaces';
import { fitText, FittedText } from './fit-text';
import { drawRisingFromLine, MaskBox } from './rise-mask';
import { baselineFromTop, fontMetrics, lineHeightOf } from './text-box';

/** A fitted text with its line geometry, measured once when a scene is built. */
export interface TextBlock extends FittedText {
  lineHeight: number;
  /** Baseline of the first line below the block top. */
  baselineOffset: number;
  widths: number[];
  /** Letter spacing it was measured with, px — set the same when drawing. */
  spacing: number;
}

export interface TextBlockOptions {
  maxWidth: number;
  size: number;
  min: number;
  font: (px: number) => string;
  maxLines?: number;
  /** CSS `line-height` as a multiple of the size; none — `normal`. */
  lineHeight?: number;
  spacing?: number;
}

/** `.mask` padding-bottom `.08em` of the mask's own font; the rise travels 115 % of the text height (`riseIn`). */
export const MASK_PAD_EM = 0.08;
export const RISE_DISTANCE = 1.15;

export const px = (value: number) => `${value}px`;

/** Air around the ink of a revealed glyph, px — keeps its antialiased edge. */
const INK_AIR = 1;

export function measureBlock(ctx: ReviewCanvasContext, text: string, options: TextBlockOptions): TextBlock {
  const spacing = options.spacing ?? 0;
  ctx.letterSpacing = px(spacing);
  const fitted = fitText(ctx, text, {
    maxWidth: options.maxWidth,
    fontSize: options.size,
    minFontSize: options.min,
    font: options.font,
    maxLines: options.maxLines,
  });
  ctx.font = fitted.font;
  const metrics = fontMetrics(ctx, fitted.fontSize);
  const lineHeight = options.lineHeight === undefined ? undefined : options.lineHeight * fitted.fontSize;
  const block: TextBlock = {
    ...fitted,
    spacing,
    lineHeight: lineHeightOf(metrics, lineHeight),
    baselineOffset: baselineFromTop(0, metrics, lineHeight),
    widths: fitted.lines.map((line) => ctx.measureText(line).width),
  };
  ctx.letterSpacing = '0px';
  return block;
}

export const blockHeight = (block: TextBlock) => block.lines.length * block.lineHeight;
export const blockWidth = (block: TextBlock) => Math.max(0, ...block.widths);

export type TextAlign = 'left' | 'center' | 'right';

/** Left edge of a line `width` wide aligned at `x` (the left edge, the centre or the right edge). */
const lineLeft = (x: number, width: number, align: TextAlign) =>
  align === 'center' ? x - width / 2 : align === 'right' ? x - width : x;

/** The lines laid out from `top`; `x` is the left edge, the centre or the right edge. Set the colour before calling. */
export function fillBlock(ctx: ReviewCanvasContext, block: TextBlock, x: number, top: number, align: TextAlign): void {
  ctx.font = block.font;
  ctx.letterSpacing = px(block.spacing);
  block.lines.forEach((line, i) => {
    ctx.fillText(line, lineLeft(x, block.widths[i], align), top + block.baselineOffset + i * block.lineHeight);
  });
}

/**
 * Grows a reveal mask over text drawn from `mask.y` until it covers the glyphs' ink, not only their line box. The
 * line box is the font's ascent and descent — glyphs reach past it: the tails of «Д», «Ц», «Щ» go under the line, the
 * breve of «Й» and the dots of «Ё» stand over the caps. A mask cut at the line box keeps clipping them once the text is
 * in place (a CSS `.mask { overflow: hidden }` does the same, so the mockup hides it on short words). 1 px of air for
 * antialiasing; without ink metrics (jsdom) the mask stays as given.
 */
export function coverInk(ctx: ReviewCanvasContext, mask: MaskBox, blocks: readonly TextBlock[]): MaskBox {
  let top = mask.y;
  let bottom = mask.y + mask.height;
  blocks.forEach((block) => {
    ctx.font = block.font;
    ctx.letterSpacing = px(block.spacing);
    block.lines.forEach((line, i) => {
      const ink = ctx.measureText(line) as Partial<TextMetrics>;
      const baseline = mask.y + block.baselineOffset + i * block.lineHeight;
      if (typeof ink.actualBoundingBoxAscent === 'number') top = Math.min(top, baseline - ink.actualBoundingBoxAscent - INK_AIR);
      if (typeof ink.actualBoundingBoxDescent === 'number')
        bottom = Math.max(bottom, baseline + ink.actualBoundingBoxDescent + INK_AIR);
    });
  });
  ctx.letterSpacing = '0px';
  return { x: mask.x, y: top, width: mask.width, height: bottom - top };
}

/**
 * Text rising from under the line below it (`.mask` > `.rise`). `maskEm` — the mask's own font size, which sets its
 * padding: it inherits the parent's font, not the text's (15 px on a plate, the text size in the intro). The mask
 * covers the glyphs' ink (`coverInk`), so a tail or an accent is never cut once the text stands.
 */
export function drawRisingBlock(
  ctx: ReviewCanvasContext,
  block: TextBlock,
  box: { x: number; top: number; width: number; align: TextAlign; maskEm?: number },
  progress: number,
): void {
  const height = blockHeight(block);
  const lineBox = {
    x: lineLeft(box.x, box.width, box.align),
    y: box.top,
    width: box.width,
    height: height + MASK_PAD_EM * (box.maskEm ?? block.fontSize),
  };
  const mask = coverInk(ctx, lineBox, [block]);
  drawRisingFromLine(ctx, mask, progress, (c) => fillBlock(c, block, box.x, box.top, box.align), RISE_DISTANCE * height);
}
